<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">
            Manage Fabrications
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <button 
            class="button is-small mb-3" 
            @click="managingTypes = true">
            Manage Fabrication Types
          </button>

          <article 
            v-if="!loading && fabrications.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no fabrications.
            </div>
          </article>

          <v-fade-transition mode="out-in">
            <div 
              v-if="loading" 
              class="is-relative">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <table 
              v-else
              class="table is-fullwidth is-hoverable">

              <thead>
                <tr>
                  <th 
                    class="text--nowrap"
                    v-for="(header, headerIndex) in headers" 
                    :key="headerIndex">
                    {{ header.label }}
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tfoot>
                <tr>
                  <td :colspan="headers.length + 1">
                    <div class="centered">
                      <button
                        v-if="!adding"
                        @click="adding = true" 
                        class="button is-primary is-small">
                        Add a new fabrication
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>

              <tbody>
                <tr 
                  class="v-centered"
                  v-for="fabrication in fabrications" 
                  :key="fabrication.id">

                  <template v-if="editingId != fabrication.id">
                    <td>
                      {{ fabrication.fabrication_name }}
                    </td>
                    <td>
                      {{ fabrication.fabrication_type.fabrication_type_name }}
                    </td>
                    <td>
                      {{ fabrication.unit ? fabrication.unit.unit_display_name : '' }}
                    </td>
                    <td>
                      {{ fabrication.unit_price || currency(2) }}
                    </td>
                    <td>
                      {{ fabrication.waste_pct || percentage }}
                    </td>
                  </template>

                  <template v-else>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Enter Fabrication Name"
                        v-model="fabrication.fabrication_name">
                      </v-text-field>
                    </td>
                    <td>
                      <v-select
                        required
                        :options="fabricationTypes"
                        item-text="fabrication_type_name"
                        item-value="id"
                        v-model="fabrication.fabrication_type_id"
                      />
                    </td>
                    <td>
                      <v-select
                        required
                        :options="units"
                        item-text="unit_display_name"
                        item-value="id"
                        v-model="fabrication.unit_id"
                      />
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Enter Fabrication Unit Price"
                        type="number"
                        v-model="fabrication.unit_price">
                      </v-text-field>
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Enter Fabrication Waste Percentage"
                        type="number"
                        v-model="fabrication.waste_pct">
                      </v-text-field>
                    </td>
                  </template>
                  <td>
                    <div class="buttons buttons-right">
                      <template v-if="editingId != fabrication.id">
                        <button
                          @click="editingId = fabrication.id" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-edit has-text-grey"></i>
                          </span>
                        </button>
                        <button
                          @click="launchConfirmDelete(fabrication.id)" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-trash has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                      <template v-else>
                        <button
                          @click="update(fabrication)" 
                          class="button is-primary is-small">
                          <span class="icon">
                            <i class="fas fa-save"></i>
                          </span>
                        </button>
                        <button
                          @click="editingId = null" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-times has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>

                <tr class="v-centered" v-if="adding">
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Fabrication Name"
                      v-model="fabrication.fabrication_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-select
                      required
                      :options="fabricationTypes"
                      item-text="fabrication_type_name"
                      item-value="id"
                      v-model="fabrication.fabrication_type_id"
                    />
                  </td>
                  <td>
                    <v-select
                      required
                      :options="units"
                      item-text="unit_display_name"
                      item-value="id"
                      v-model="fabrication.unit_id"
                    />
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Fabrication Unit Price"
                      type="number"
                      v-model="fabrication.unit_price">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Fabrication Waste Percentage"
                      type="number"
                      v-model="fabrication.waste_pct">
                    </v-text-field>
                  </td>
                  <td>
                    <div class="buttons buttons-right">
                      <button
                        @click="add" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-fade-transition>

        </div>

        <v-confirm-modal 
          v-model="confirmDelete.state"
          :title="confirmDelete.title"
          :message="confirmDelete.message"
          :type="confirmDelete.type"
          @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
          @cancel="confirmDelete.state = false"/>

        <manage-fabrication-types-modal v-model="managingTypes"/>

      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VSpinLoader from '@/components/base/VSpinLoader'
import VConfirmModal from '@/components/base/VConfirmModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import ManageFabricationTypesModal from '@/components/estimations/ManageFabricationTypesModal'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { 
    VTextField, VSelect, VSpinLoader, VConfirmModal, VFadeTransition,
    ManageFabricationTypesModal 
  },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Type' },
        { label: 'Unit Type' },
        { label: 'Unit Price' },
        { label: 'Waste %' } 
      ],
      fabrication: {
        fabrication_name: '',
        fabrication_type_id: null,
        unit_id: '',
        unit_price: '',
        waste_pct: null
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this fabrication?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null,
      managingTypes: false
    }
  },
  computed: {
    ...mapState("fabrications", [
      "fabrications", "fabricationTypes", "loading", "typesLoading"
    ]),
    ...mapState("units", ["units"])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("fabrications", [
      "getFabrications","addFabrication", "deleteFabrication", 
      "updateFabrication", "getFabricationTypes", "toggleAllSelected"
    ]),
    ...mapMutations("fabrications", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    ...mapActions("units", ["getUnits"]),
    initialize() {
      if (this.fabrications.length == 0) {
        this.getFabrications();
      }
      if (this.fabricationTypes.length == 0) {
        this.getFabricationTypes();
      }
      if (this.units.length == 0) {
        this.getUnits();
      }
    },
    add() {
      this.addFabrication(this.fabrication).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(fabrication) {
      let data = {};
      Object.keys(this.fabrication).forEach(field => {
        data[field] = fabrication[field]
      });

      this.updateFabrication(
        { fabricationId: fabrication.id, data: data }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteFabrication(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this fabrication?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-content {
  width: 1000px;
}

.modal-card {
  border-radius: 6px;
  width: 1000px;
}
</style>
